<template>
  <div class="custom-canvas" :style="{ left: startXPosition + 'px', top: startYPosition + 'px' }">
    <canvas ref="canvas"></canvas>
  </div>
</template>
<script>
const padding = 6;
const stylesMap = {
  bgColor: "backgroundColor",
  verticalAlign: "vAlign",
  color: "fontColor",
  textAlign: "align",
  fontWeight: "fontWeight",
  fontStyle: "fontStyle",
  fontSize: "fontSize",
  fontFamily: "fontFamily",
};
const textProperties = ["fontWeight", "color", "fontStyle", "fontSize", "textAlign", "fontFamily", "verticalAlign"];

import { drawText } from "text-to-canvas";
import DesignerMixin from "../process-design/DesignerMixin";
import { getDefaultCellType } from "../process-design/cell/Utils";
import { formatNumberToFitColumn, isFormula } from "../../utils/util";

import { defaultStyles, defaultThemes } from "../process-design/cell/CellConstants";
import { tree } from "../../store/RangeStyle";
import { formatCurrency, formatDate, formatFraction, formatTime, NumberFormats } from "../../utils/DataTypeUtils";

export default {
  mixins: [DesignerMixin],
  props: [
    "startYPosition",
    "startXPosition",
    "visibleTableWidth",
    "visibleTableHeight",
    "getPxFromRowIndex",
    "getPxFromColumnIndex",
    "options",
  ],
  data() {
    return {
      preloadedImages: {},
    };
  },
  computed: {
    filteredCells() {
      return this.$store.state.cells.filteredCells;
    },
    componentOptions() {
      return { ...(this.options || {}) };
    },
    hScroll() {
      return this.$store.state.cells.hScroll;
    },
    vScroll() {
      return this.$store.state.cells.vScroll;
    },
    tableHeaders() {
      return this.$store.state.table.headers;
    },
    tableLastCellIds() {
      return this.$store.state.table.lastCellIds;
    },
  },
  watch: {
    hScroll(newValue, oldValue) {
      this.drawCells();
    },
    vScroll(newValue, oldValue) {
      this.drawCells();
    },
    redrawCanvas() {
      this.drawCells();
    },
    options() {
      this.drawCells();
    },
    visibleTableHeight() {
      this.$nextTick(() => {
        this.scaleCanvas();
        this.drawCells();
      });
    },
    visibleTableWidth(newValue, oldValue) {
      this.$nextTick(() => {
        this.scaleCanvas();
        this.drawCells();
      });
    },
  },
  mounted() {
    this.preloadedImages = {};
    const allIcons = [
      "Info",
      "ArrowDown",
      "Help",
      "Alert",
      "Progress",
      "AccountReactivate",
      "icon-plus-circle",
      "icon-minus-circle",
      "arrow-down",
    ];
    const allSvgIcons = ["table-L-shape", "filter", "asc", "desc", "filter-asc", "filter-desc", "Dropdown1"];
    allIcons.forEach((icon) => {
      const image = new Image();
      image.src = require(`@/assets/icons/${icon}.png`);
      this.preloadedImages[icon] = image;
    });
    allSvgIcons.forEach((icon) => {
      const image = new Image();
      image.src = require(`@/assets/icons/${icon}.svg`);
      this.preloadedImages[icon] = image;
    });

    this.scaleCanvas();
    this.drawCells();
  },
  methods: {
    scaleCanvas() {
      const canvas = this.$refs.canvas;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      // canvas does not update it's width and height after new row or column is added
      // hence the values 200 and 100
      canvas.width = (this.visibleTableWidth + 200) * 2;
      canvas.height = (this.visibleTableHeight + 100) * 2;
      canvas.style.width = this.visibleTableWidth + 200 + "px";
      canvas.style.height = this.visibleTableHeight + 100 + "px";
      ctx.scale(2, 2);
    },
    getMergeCellSize(mergeId) {
      const mergedCell = this.mergedCells[mergeId];
      const startXPosition = this.getPxFromColumnIndex(this.columnMap[mergedCell.columns.startColumnId].index);
      const endXPosition =
        this.getPxFromColumnIndex(this.columnMap[mergedCell.columns.endColumnId].index) +
        this.getRawWidth(mergedCell.columns.endColumnId);

      const startYPosition = this.getPxFromRowIndex(this.rowMap[mergedCell.rows.startRowId].index);
      const endYPosition =
        this.getPxFromRowIndex(this.rowMap[mergedCell.rows.endRowId].index) +
        this.getRawHeight(mergedCell.rows.endRowId);

      const width = endXPosition - startXPosition;
      const height = endYPosition - startYPosition;
      return { width, height };
    },

    drawCells() {
      const canvas = this.$refs.canvas;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, this.visibleTableWidth, this.visibleTableHeight);
      for (let i = this.vScroll.index; i < this.vScroll.end; i++) {
        for (let j = this.hScroll.index; j < this.hScroll.end; j++) {
          const row = this.rows[i];
          const column = this.columns[j];
          const margin = this.componentOptions.margin;
          const cellId = `${row.id}_${column.id}`;
          let columnWidth = this.getRawWidth(column.id) - 2 * margin;
          let rowHeight = this.getRawHeight(row.id) - 2 * margin;

          const yPosition = this.getPxFromRowIndex(i) - this.startYPosition + margin;
          const xPosition = this.getPxFromColumnIndex(j) - this.startXPosition + margin;

          ctx.strokeStyle = "rgb(236,236,236)";
          const properties = this.$store.getters["rangeStyle/properties"](j, i);

          if (this.mergedCells[`${cellId}_merged`] && properties.bgColor) {
            let { width, height } = this.getMergeCellSize(`${cellId}_merged`);
            columnWidth = width - 2 * margin;
            rowHeight = height - 2 * margin;
            this.drawCell(ctx, xPosition, yPosition, columnWidth, rowHeight, properties.bgColor);
          } else if (this.mergedCellIds && this.mergedCellIds[cellId]) continue;
          else if (columnWidth <= 0 || rowHeight <= 0) continue;
          else if (properties.bgColor)
            if (this.filteredCells[`${i}_${j}`]) {
              this.drawCell(ctx, xPosition, yPosition, columnWidth, rowHeight, "yellow");
            } else {
              this.drawCell(ctx, xPosition, yPosition, columnWidth, rowHeight, properties.bgColor);
            }
          if (this.componentOptions.lines) {
            this.drawLines(ctx, xPosition, yPosition, columnWidth, rowHeight);
          }
          if (this.cellsMetaData[cellId]?.comment) {
            this.drawCommentIndicator(ctx, xPosition, yPosition, columnWidth, rowHeight);
          }
          if (this.cellsMetaData[cellId]?.icons) {
            const cellIcons = (this.cellsMetaData[cellId]?.icons.split(",") || []).filter((item) => item);
            this.drawIcons(ctx, xPosition, yPosition, columnWidth, rowHeight, cellIcons);
          }
          let text = this.getCellValue(i, j);
          const expression = this.getCellText(i, j);
          const shouldFormat = text !== "" && isFormula(expression.toString()) && !isNaN(text) && text % 1 !== 0;
          if (shouldFormat) text = formatNumberToFitColumn(text, columnWidth, ctx);

          if (text !== null && text !== undefined && text !== "" && properties.dataType) {
            text = this.getValueByDataType(text, properties.dataType);
          }
          drawText(ctx, text + "", {
            x: xPosition + padding,
            y: yPosition + padding,
            width: columnWidth - 2 * padding,
            height: rowHeight - 2 * padding,
            fontSize: 14,
            align: properties.textAlign ?? isNaN(text) ? "left" : "right",
            vAlign: properties.verticalAlign ?? "top",
            overflow: false,
            fontFamily: "Roboto, sans-serif",
            ...this.pick(properties, textProperties),
          });

          if (this.tableHeaders[cellId] && this.tableHeaders[cellId].visible) {
            let icon = "Dropdown1";
            if (this.tableHeaders[cellId].sort && Object.keys(this.tableHeaders[cellId].sort).length !== 0) {
              if (this.tableHeaders[cellId]?.filters?.cellId) {
                icon = "filter-" + this.tableHeaders[cellId].sort;
              } else {
                icon = this.tableHeaders[cellId].sort;
              }
            } else if (this.tableHeaders[cellId]?.filters?.cellId) {
              icon = "filter";
            }
            this.drawTableHeader(ctx, xPosition, yPosition, columnWidth, rowHeight, icon);
          }
          if (this.tableLastCellIds[cellId]) {
            let icon = "table-L-shape";
            this.drawLShape(ctx, xPosition, yPosition, columnWidth, rowHeight, icon);
          }
        }
      }
    },
    drawLines(ctx, xPosition, yPosition, columnWidth, rowHeight) {
      ctx.beginPath();
      ctx.lineWidth = 1;
      ctx.shadowBlur = 0;
      ctx.strokeStyle = "rgb(220, 220, 220)";
      ctx.moveTo(xPosition + columnWidth - 0.5, yPosition - 0.5);
      ctx.lineTo(xPosition + columnWidth - 0.5, yPosition + rowHeight - 0.5);
      ctx.lineTo(xPosition - 0.5, yPosition + rowHeight - 0.5);
      ctx.stroke();
    },
    drawCell(ctx, xPosition, yPosition, columnWidth, rowHeight, backgroundColor) {
      ctx.fillStyle = backgroundColor;
      if (this.componentOptions.borderRadius) {
        ctx.beginPath();
        ctx.roundRect(xPosition, yPosition, columnWidth, rowHeight, this.componentOptions.borderRadius);
        ctx.fill();
      } else {
        ctx.fillRect(xPosition, yPosition, columnWidth, rowHeight);
      }
    },
    drawCommentIndicator(ctx, xPosition, yPosition, columnWidth, rowHeight) {
      const radius = this.componentOptions.borderRadius;
      const diff = 4.5; // this is used for the size of comment indicator
      const rightX = xPosition + columnWidth - 0.5;
      ctx.fillStyle = "red";
      ctx.beginPath();
      ctx.moveTo(rightX - 2 * diff, yPosition);
      ctx.lineTo(rightX - diff, yPosition);
      ctx.arcTo(rightX, yPosition, rightX, yPosition + diff, radius);
      ctx.lineTo(rightX, yPosition + 2 * diff);
      ctx.closePath();
      ctx.fill();
    },
    drawIcons(ctx, xPosition, yPosition, columnWidth, rowHeight, icons) {
      icons.forEach((icon, index) => {
        const image = this.preloadedImages[icon];
        const width = 18;
        const height = 18;
        if (icon === "icon-plus-circle" || icon === "icon-minus-circle") {
          xPosition += columnWidth / 2 - width / 2;
          yPosition += rowHeight / 2 - height / 2;
          ctx.drawImage(image, xPosition, yPosition, width, height);
        } else {
          const rightPadding = 2;
          const x = xPosition + columnWidth - (index + 1) * (width + rightPadding);
          const y = yPosition + rowHeight / 2 - height / 2 - rightPadding;
          ctx.drawImage(image, x, y, width, height);
        }
      });
    },
    pick(obj, props) {
      const result = {};
      props.forEach((prop) => {
        if (obj[prop]) result[stylesMap[prop]] = obj[prop];
      });
      return result;
    },
    drawTableHeader(ctx, xPosition, yPosition, columnWidth, rowHeight, icon) {
      const image = this.preloadedImages[icon];
      const width = 18;
      const height = 18;
      xPosition += columnWidth - width;
      yPosition += rowHeight - height;
      ctx.drawImage(image, xPosition, yPosition, width, height);
    },
    drawLShape(ctx, xPosition, yPosition, columnWidth, rowHeight, icon) {
      const image = this.preloadedImages[icon];
      const width = 10;
      const height = 10;
      xPosition += columnWidth - width;
      yPosition += rowHeight - height;
      ctx.drawImage(image, xPosition, yPosition, width, height);
    },
    getValueByDataType(text, type) {
      if (isNaN(text)) return text;
      switch (type) {
        case NumberFormats.Date:
        case NumberFormats.ShortDate:
        case NumberFormats.LongDate:
          return formatDate(text, type);
        case NumberFormats.Currency:
          return formatCurrency(text);
        case NumberFormats.Number:
          return parseFloat(text).toFixed(2);
        case NumberFormats.Time:
          return formatTime(text);
        case NumberFormats.Fraction:
          return formatFraction(text);
        case NumberFormats.Percentage:
          return text * 100 + "%";
        default:
          return text;
      }
    },
  },
};
</script>

<style>
body {
  margin: 0;
}
.custom-canvas {
  position: absolute;
  padding: 25px 52px;
}
</style>
